import {
  Button,
  DatePicker,
  Dropdown,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextInputTypes,
  ETextStyleVariant,
  Heading,
  Text,
  TextInput,
} from '@outdoorsyco/bonfire';
import { isBefore, startOfDay } from 'date-fns';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { DateRange, Matcher } from 'react-day-picker';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import MaxWidth from '@/components/common/layout/MaxWidth';
import { PhoneNumberInput } from '@/components/ui/PhoneNumberInput';
import { usePhoneValidatorBuilder } from '@/constants/validationRules';
import { useProfileCountry } from '@/hooks/useProfileCountry';
import { trackGroupBookingRequestSubmittedEvent } from '@/services/analytics/group-bookings';
import {
  IGroupBookingEnquiryPayload,
  submitGroupBookingEnquiry,
} from '@/services/groupRvBookingForm';

type GroupRvEnquiryFormData = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  eventDates: DateRange;
  numberOfRvs: string;
  location: string;
  eventType: string;
  fullHookups: string;
  primaryRvUse: string;
  rentalCars: string;
  groupSize: string;
  approximateBudget: string;
};

export const buildGroupBookingEnquiryPayload = (
  data: GroupRvEnquiryFormData,
): IGroupBookingEnquiryPayload => ({
  first_name: data.firstName.trim(),
  last_name: data.lastName.trim(),
  phone_number: data.phoneNumber,
  email: data.email.trim(),
  event_start_date: data.eventDates.from ? dayjs(data.eventDates.from).format('YYYY-MM-DD') : '',
  event_end_date: data.eventDates.to ? dayjs(data.eventDates.to).format('YYYY-MM-DD') : '',
  number_of_rvs: parseInt(data.numberOfRvs, 10) || 0,
  location: data.location.trim(),
  event_type: data.eventType,
  full_hookups: data.fullHookups,
  primary_rv_use: data.primaryRvUse,
  rental_cars: data.rentalCars,
  group_size_range: data.groupSize,
  approximate_budget_range: data.approximateBudget,
});

interface GroupRvEnquiryFormProps {
  page: string;
}

export const GroupRvEnquiryForm: React.FC<GroupRvEnquiryFormProps> = ({ page }) => {
  const intl = useIntl();
  const buildPhoneValidator = usePhoneValidatorBuilder();
  const profileCountry = useProfileCountry();

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const disablePastDates: Matcher = (date: Date) => isBefore(date, startOfDay(new Date()));

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GroupRvEnquiryFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      eventDates: { from: undefined, to: undefined },
      numberOfRvs: '',
      location: '',
      eventType: '',
      fullHookups: '',
      primaryRvUse: '',
      rentalCars: '',
      groupSize: '',
      approximateBudget: '',
    },
  });

  const selectLabel = intl.formatMessage({
    id: 'kQAf2d',
    defaultMessage: 'Select',
  });

  const onSubmit = async (data: GroupRvEnquiryFormData) => {
    setSubmitError(null);
    setSubmitting(true);

    try {
      await submitGroupBookingEnquiry(buildGroupBookingEnquiryPayload(data));
      trackGroupBookingRequestSubmittedEvent({
        page,
        eventType: data.eventType,
        location: data.location.trim(),
        numberOfRvs: parseInt(data.numberOfRvs, 10) || 0,
        groupSize: data.groupSize,
      });
      setSubmitted(true);
      reset();
    } catch {
      setSubmitError(
        intl.formatMessage({
          id: 'riqnVv',
          defaultMessage: 'Something went wrong. Please try again.',
          description: 'Group RV enquiry form > Submit error message',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const firstNameLabel = intl.formatMessage({
    id: 'pONqz8',
    defaultMessage: 'First name',
  });

  const lastNameLabel = intl.formatMessage({
    id: 'txUL0F',
    defaultMessage: 'Last name',
  });

  const phoneNumberLabel = intl.formatMessage({
    id: 'jdJhOL',
    defaultMessage: 'Phone number',
  });

  const emailLabel = intl.formatMessage({
    id: 'sy+pv5',
    defaultMessage: 'Email',
  });

  const numberOfRvsLabel = intl.formatMessage({
    id: 'SsLK+H',
    defaultMessage: 'Number of RVs needed',
  });

  const locationLabel = intl.formatMessage({
    id: 'rvirM2',
    defaultMessage: 'Location',
  });

  const eventTypeLabel = intl.formatMessage({
    id: 'ZYPJGM',
    defaultMessage: 'Type of event',
    description: 'Group RV enquiry form > Event type label',
  });

  const fullHookupsLabel = intl.formatMessage({
    id: 'vGENw/',
    defaultMessage: 'Does your location have full hookups?',
    description: 'Group RV enquiry form > Full hookups label',
  });

  const rentalCarsLabel = intl.formatMessage({
    id: 'wDj4yE',
    defaultMessage: 'Will your group need rental cars?',
    description: 'Group RV enquiry form > Rental cars label',
  });

  const primaryRvUseLabel = intl.formatMessage({
    id: 'RntTDB',
    defaultMessage: 'Primary RV use for your group',
    description: 'Group RV enquiry form > Primary RV use label',
  });

  const groupSizeLabel = intl.formatMessage({
    id: 'Q20zjO',
    defaultMessage: 'How many people are you booking for?',
    description: 'Group RV enquiry form > Group size label',
  });

  const budgetLabel = intl.formatMessage({
    id: 'kN2z0+',
    defaultMessage: 'What is your approximate budget?',
    description: 'Group RV enquiry form > Budget label',
  });

  const eventDatesFromLabel = intl.formatMessage({
    id: 'dM+p3/',
    defaultMessage: 'From',
  });

  const eventDatesToLabel = intl.formatMessage({
    id: '9j3hXO',
    defaultMessage: 'To',
  });

  const saveLabel = intl.formatMessage({
    id: 'jvo0vs',
    defaultMessage: 'Save',
  });

  const clearLabel = intl.formatMessage({
    id: '/GCoTA',
    defaultMessage: 'Clear',
  });

  const eventDatesAriaLabel = intl.formatMessage({
    id: 'btCDxI',
    defaultMessage: 'Event dates',
    description: 'Group RV enquiry form > Event dates label',
  });

  const eventTypeOptions = [
    {
      value: 'wedding',
      label: intl.formatMessage({ id: 'Idg+3/', defaultMessage: 'Wedding' }),
    },
    {
      value: 'festival',
      label: intl.formatMessage({ id: '5zcXRy', defaultMessage: 'Cultural event' }),
    },
    {
      value: 'staff',
      label: intl.formatMessage({ id: 'OesI6U', defaultMessage: 'Staff housing' }),
    },
    {
      value: 'film',
      label: intl.formatMessage({ id: 'QyIitN', defaultMessage: 'Film production' }),
    },
    {
      value: 'sporting',
      label: intl.formatMessage({
        id: 'OD8VgS',
        defaultMessage: 'Festival or sporting event',
      }),
    },
    {
      value: 'other',
      label: intl.formatMessage({ id: '/VnDMl', defaultMessage: 'Other' }),
    },
  ];

  // Shared with the rental cars question, which offers the same options.
  const yesNoUnsureOptions = [
    {
      value: 'yes',
      label: intl.formatMessage({ id: 'a5msuh', defaultMessage: 'Yes' }),
    },
    {
      value: 'no',
      label: intl.formatMessage({ id: 'oUWADl', defaultMessage: 'No' }),
    },
    {
      value: 'unsure',
      label: intl.formatMessage({ id: 'AkIyKO', defaultMessage: "I don't know" }),
    },
  ];

  const primaryRvUseOptions = [
    {
      value: 'sleeping',
      label: intl.formatMessage({ id: 'Rustnr', defaultMessage: 'Sleeping accommodations' }),
    },
    {
      value: 'Tailgating',
      label: intl.formatMessage({ id: 'dVH+xp', defaultMessage: 'Tailgating / Gameday Hub' }),
    },
    {
      value: 'Transportation',
      label: intl.formatMessage({ id: '/x7aOj', defaultMessage: 'Transportation' }),
    },
    {
      value: 'talent',
      label: intl.formatMessage({
        id: 'se5/72',
        defaultMessage: 'Talent Trailer / Wardrobe',
      }),
    },
    {
      value: 'Bridal',
      label: intl.formatMessage({ id: 'UiZCEs', defaultMessage: 'Bridal / Groom Suite' }),
    },
    {
      value: 'other',
      label: intl.formatMessage({ id: '/VnDMl', defaultMessage: 'Other' }),
    },
  ];

  const groupSizeOptions = [
    { value: '5-10', label: intl.formatMessage({ id: 'WMy5GV', defaultMessage: '5-10' }) },
    { value: '10-20', label: intl.formatMessage({ id: 'FZ5PCG', defaultMessage: '10-20' }) },
    { value: '20-30', label: intl.formatMessage({ id: '8D8X7o', defaultMessage: '20-30' }) },
    {
      value: 'other',
      label: intl.formatMessage({ id: '/VnDMl', defaultMessage: 'Other' }),
    },
  ];

  const budgetOptions = [
    {
      value: '$150-$200',
      label: intl.formatMessage({
        id: 'A+fZbc',
        defaultMessage: '$150-$200 per RV per night',
      }),
    },
    {
      value: '$200-$300',
      label: intl.formatMessage({
        id: 'I3c0DT',
        defaultMessage: '$200-$300 per RV per night',
      }),
    },
    {
      value: '$300-$400',
      label: intl.formatMessage({
        id: '7dvQq7',
        defaultMessage: '$300-$400 per RV per night',
      }),
    },
  ];

  return (
    <section id="group-bookings-form" className="py-12 bg-white border-t border-gray-200 lg:py-16">
      <div className="flex justify-center lg:px-12">
        <MaxWidth className="gap-6">
          <Heading
            variant={EHeadingStyleVariant.H3}
            className="mb-4 leading-tight text-gray-900 font-[abril-titling] !text-[40px]">
            {intl.formatMessage({
              id: 'q4Td3z',
              defaultMessage: 'Submit your group booking enquiry now.',
              description: 'Group bookings form heading',
            })}
          </Heading>

          <Text
            variant={ETextStyleVariant.MediumRegular}
            className="max-w-3xl mb-5 whitespace-pre-line text-gray-70">
            {intl.formatMessage({
              id: 'ZKW+By',
              defaultMessage:
                'From weddings to film sets, cultural festivals to construction sites—group RV rentals give you comfort, flexibility, and control. Forget hotel headaches and long drives between locations. We bring the rooms to you.',
              description: 'Group bookings form body copy',
            })}
          </Text>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6" noValidate>
            {/* Row 1: Name */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: intl.formatMessage({
                    id: 'xScUKF',
                    defaultMessage: 'First name is required',
                    description: 'Group RV enquiry form > First name required error',
                  }),
                }}
                render={({ field }) => (
                  <TextInput
                    controlled
                    name="firstName"
                    label={firstNameLabel}
                    ariaLabel={firstNameLabel}
                    inputValue={field.value}
                    onChange={field.onChange}
                    markAsRequired
                    error={!!errors.firstName}
                    helpText={errors.firstName?.message}
                  />
                )}
              />

              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: intl.formatMessage({
                    id: 'w7c4d6',
                    defaultMessage: 'Last name is required',
                    description: 'Group RV enquiry form > Last name required error',
                  }),
                }}
                render={({ field }) => (
                  <TextInput
                    controlled
                    name="lastName"
                    label={lastNameLabel}
                    ariaLabel={lastNameLabel}
                    inputValue={field.value}
                    onChange={field.onChange}
                    markAsRequired
                    error={!!errors.lastName}
                    helpText={errors.lastName?.message}
                  />
                )}
              />
            </div>

            {/* Row 2: Phone + Email — a row each, since half the grid leaves the number too narrow beside its country dropdown */}
            <div className="grid grid-cols-1 gap-6">
              <Controller
                name="phoneNumber"
                control={control}
                rules={buildPhoneValidator()}
                render={({ field }) => (
                  <PhoneNumberInput
                    name="phoneNumber"
                    label={phoneNumberLabel}
                    value={field.value}
                    onChange={field.onChange}
                    userCountry={profileCountry}
                    markAsRequired
                    error={!!errors.phoneNumber}
                    helpText={errors.phoneNumber?.message}
                  />
                )}
              />

              <Controller
                name="email"
                control={control}
                rules={{
                  required: intl.formatMessage({
                    id: 'FYMGdw',
                    defaultMessage: 'Email is required',
                    description: 'Group RV enquiry form > Email required error',
                  }),
                  pattern: {
                    value: /^\S+@\S+\.\S+$/,
                    message: intl.formatMessage({
                      id: '26QWub',
                      defaultMessage: 'Please enter a valid email address',
                      description: 'Group RV enquiry form > Email pattern error',
                    }),
                  },
                }}
                render={({ field }) => (
                  <TextInput
                    controlled
                    name="email"
                    label={emailLabel}
                    ariaLabel={emailLabel}
                    inputValue={field.value}
                    onChange={field.onChange}
                    markAsRequired
                    error={!!errors.email}
                    helpText={errors.email?.message}
                  />
                )}
              />
            </div>

            {/* Row 3: Event dates (Bonfire DatePicker range) */}
            <Controller
              name="eventDates"
              control={control}
              rules={{
                validate: value => {
                  if (!value?.from || !value?.to) {
                    return intl.formatMessage({
                      id: 'f8DrOE',
                      defaultMessage: 'Event dates are required',
                      description: 'Group RV enquiry form > Event dates required error',
                    });
                  }
                  return true;
                },
              }}
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  ariaDialogLabel={eventDatesAriaLabel}
                  ariaLabelFrom={eventDatesFromLabel}
                  ariaLabelTo={eventDatesToLabel}
                  mode="range"
                  onChange={e => {
                    if (e?.from && e?.to) {
                      onChange(e);
                    } else {
                      onChange({ from: undefined, to: undefined });
                    }
                  }}
                  saveButtonLabel={saveLabel}
                  clearLabel={value?.from ? clearLabel : ''}
                  labelFrom={eventDatesFromLabel}
                  labelTo={eventDatesToLabel}
                  size="full"
                  selected={{ from: value?.from, to: value?.to }}
                  disabled={disablePastDates}
                  error={!!errors.eventDates}
                  helpText={errors.eventDates?.message}
                />
              )}
            />

            {/* Row 4: RVs + Location */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Controller
                name="numberOfRvs"
                control={control}
                rules={{
                  required: intl.formatMessage({
                    id: '0FPy88',
                    defaultMessage: 'Please enter the number of RVs needed',
                    description: 'Group RV enquiry form > Number of RVs required error',
                  }),
                  validate: value => {
                    const parsed = parseInt(value, 10);
                    if (!Number.isFinite(parsed) || parsed <= 0) {
                      return intl.formatMessage({
                        id: 'VwLn3r',
                        defaultMessage: 'Number of RVs must be at least 1',
                        description: 'Group RV enquiry form > Number of RVs min error',
                      });
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextInput
                    controlled
                    name="numberOfRvs"
                    label={numberOfRvsLabel}
                    ariaLabel={numberOfRvsLabel}
                    inputValue={field.value}
                    onChange={field.onChange}
                    type={ETextInputTypes.Number}
                    error={!!errors.numberOfRvs}
                    helpText={errors.numberOfRvs?.message}
                  />
                )}
              />

              <Controller
                name="location"
                control={control}
                rules={{
                  required: intl.formatMessage({
                    id: 'Bz0Pa6',
                    defaultMessage: 'Location is required',
                    description: 'Group RV enquiry form > Location required error',
                  }),
                }}
                render={({ field }) => (
                  <TextInput
                    controlled
                    name="location"
                    label={locationLabel}
                    ariaLabel={locationLabel}
                    inputValue={field.value}
                    onChange={field.onChange}
                    markAsRequired
                    error={!!errors.location}
                    helpText={errors.location?.message}
                  />
                )}
              />
            </div>

            {/* Row 5: Event type + Full hookups */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Controller
                name="eventType"
                control={control}
                rules={{
                  required: intl.formatMessage({
                    id: 'VAEwaO',
                    defaultMessage: 'Please select a type of event',
                    description: 'Group RV enquiry form > Event type required error',
                  }),
                }}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    label={eventTypeLabel}
                    ariaLabel={eventTypeLabel}
                    name="eventType"
                    inputValue={value}
                    onChange={onChange}
                    searchPlaceholder={selectLabel}
                    error={!!errors.eventType}
                    helperText={errors.eventType?.message}>
                    {eventTypeOptions.map(opt => (
                      <Dropdown.Option
                        key={opt.value}
                        value={opt.value}
                        content={{ label: opt.label }}
                      />
                    ))}
                  </Dropdown>
                )}
              />

              <Controller
                name="fullHookups"
                control={control}
                rules={{
                  required: intl.formatMessage({
                    id: 'r2ggm1',
                    defaultMessage: 'Please select whether your location has full hookups',
                    description: 'Group RV enquiry form > Full hookups required error',
                  }),
                }}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    label={fullHookupsLabel}
                    ariaLabel={fullHookupsLabel}
                    name="fullHookups"
                    inputValue={value}
                    onChange={onChange}
                    searchPlaceholder={selectLabel}
                    error={!!errors.fullHookups}
                    helperText={errors.fullHookups?.message}>
                    {yesNoUnsureOptions.map(opt => (
                      <Dropdown.Option
                        key={opt.value}
                        value={opt.value}
                        content={{ label: opt.label }}
                      />
                    ))}
                  </Dropdown>
                )}
              />
            </div>

            {/* Row 6: Primary use + Group size */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Controller
                name="primaryRvUse"
                control={control}
                rules={{
                  required: intl.formatMessage({
                    id: 'l9CN+T',
                    defaultMessage: 'Please select primary RV use for your group',
                    description: 'Group RV enquiry form > Primary RV use required error',
                  }),
                }}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    label={primaryRvUseLabel}
                    ariaLabel={primaryRvUseLabel}
                    name="primaryRvUse"
                    inputValue={value}
                    onChange={onChange}
                    searchPlaceholder={selectLabel}
                    error={!!errors.primaryRvUse}
                    helperText={errors.primaryRvUse?.message}>
                    {primaryRvUseOptions.map(opt => (
                      <Dropdown.Option
                        key={opt.value}
                        value={opt.value}
                        content={{ label: opt.label }}
                      />
                    ))}
                  </Dropdown>
                )}
              />

              <Controller
                name="groupSize"
                control={control}
                rules={{
                  required: intl.formatMessage({
                    id: '28HrMZ',
                    defaultMessage: 'Please select how many people you are booking for',
                    description: 'Group RV enquiry form > Group size required error',
                  }),
                }}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    label={groupSizeLabel}
                    ariaLabel={groupSizeLabel}
                    name="groupSize"
                    inputValue={value}
                    onChange={onChange}
                    searchPlaceholder={selectLabel}
                    error={!!errors.groupSize}
                    helperText={errors.groupSize?.message}>
                    {groupSizeOptions.map(opt => (
                      <Dropdown.Option
                        key={opt.value}
                        value={opt.value}
                        content={{ label: opt.label }}
                      />
                    ))}
                  </Dropdown>
                )}
              />
            </div>

            {/* Row 7: Budget + Rental cars */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Controller
                name="approximateBudget"
                control={control}
                rules={{
                  required: intl.formatMessage({
                    id: 'Q1TErr',
                    defaultMessage: 'Please select your approximate budget',
                    description: 'Group RV enquiry form > Budget required error',
                  }),
                }}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    label={budgetLabel}
                    ariaLabel={budgetLabel}
                    name="approximateBudget"
                    inputValue={value}
                    onChange={onChange}
                    searchPlaceholder={selectLabel}
                    error={!!errors.approximateBudget}
                    helperText={errors.approximateBudget?.message}>
                    {budgetOptions.map(opt => (
                      <Dropdown.Option
                        key={opt.value}
                        value={opt.value}
                        content={{ label: opt.label }}
                      />
                    ))}
                  </Dropdown>
                )}
              />

              <Controller
                name="rentalCars"
                control={control}
                rules={{
                  required: intl.formatMessage({
                    id: 'ZXp4Qo',
                    defaultMessage: 'Please select whether your group needs rental cars',
                    description: 'Group RV enquiry form > Rental cars required error',
                  }),
                }}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    label={rentalCarsLabel}
                    ariaLabel={rentalCarsLabel}
                    name="rentalCars"
                    inputValue={value}
                    onChange={onChange}
                    searchPlaceholder={selectLabel}
                    error={!!errors.rentalCars}
                    helperText={errors.rentalCars?.message}>
                    {yesNoUnsureOptions.map(opt => (
                      <Dropdown.Option
                        key={opt.value}
                        value={opt.value}
                        content={{ label: opt.label }}
                      />
                    ))}
                  </Dropdown>
                )}
              />
            </div>

            {submitError && (
              <div className="p-4 text-red-700 border border-red-300 rounded-md bg-red-50">
                {submitError}
              </div>
            )}
            {submitted && (
              <div className="p-4 text-green-700 border border-green-300 rounded-md bg-green-50">
                {intl.formatMessage({
                  id: 'ZeXHHB',
                  defaultMessage:
                    'Thank you! Your enquiry has been received. Our team will contact you shortly.',
                  description: 'Group RV enquiry form > Success message',
                })}
              </div>
            )}

            <div className="flex mt-2">
              <Button
                variant={EButtonColorVariant.Primary}
                className="w-[50%] sm:w-auto"
                label={intl.formatMessage({
                  id: 'TimxWT',
                  defaultMessage: 'Submit',
                  description: 'Group bookings form submit CTA',
                })}
                type="submit"
                disabled={submitting}
              />
            </div>
          </form>
        </MaxWidth>
      </div>
    </section>
  );
};
